import React from 'react'
import { useMutation } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { useClient } from '@hooks/useClient'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { CampaignTypeEnum } from '@interfaces/promotionCampaign/CampaignTypeEnum'
import { CampaignStatusEnum } from '@interfaces/promotionCampaign/CampaignStatusEnum'
import { CampaignFormType } from '@models/promotionCampaign/CampaignFormType'
import { CreateCampaignPageProps } from './interface'

export function withCreateCampaignPage(
  Component: React.FC<CreateCampaignPageProps>
) {
  function WithCreateCampaignPage() {
    const { type } = useParams()
    const navigate = useNavigate()
    const client = useClient()
    const alert = useAlert()
    const confirmModal = useModal({ modal: 'confirm' })

    const campaignType =
      (type as CampaignTypeEnum) || CampaignTypeEnum.PER_CHAPTER

    const initialValues: CampaignFormType = {
      name: '',
      type: campaignType,
      status: CampaignStatusEnum.PENDING,
      startDate: undefined,
      endDate: undefined,
      wholeStoryDiscountType: undefined,
      freeReadingType: undefined,
      bookData: [],
      csvFile: undefined,
    }

    const { mutateAsync: createCampaign } = useMutation(
      (values: CampaignFormType) =>
        client!.promotionCampaignClient.createCampaign(values),
      {
        onSuccess: () => {
          alert.success('สร้างแคมเปญสำเร็จ')
          confirmModal.hide()
          navigate('/promotion-campaign')
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง')
          confirmModal.hide()
        },
      }
    )

    const { mutateAsync: createDraft } = useMutation(
      (values: CampaignFormType) =>
        client!.promotionCampaignClient.createCampaign({
          ...values,
          status: CampaignStatusEnum.DRAFT,
        }),
      {
        onSuccess: () => {
          alert.success('บันทึกแบบร่างสำเร็จ')
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง')
        },
      }
    )

    async function handleSubmit(values: CampaignFormType) {
      await createCampaign(values)
    }

    function handleLeave(values: CampaignFormType) {
      const canSaveDraft = !!values.name.trim()

      confirmModal.show({
        confirmButtonText: 'บันทึกแบบร่าง',
        closeButtonText: 'ออกจากหน้านี้',
        hideConfirmButton: !canSaveDraft,
        content: (
          <div className='w-[320px] mb-[20px]'>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium'>
              ออกจากการสร้างแคมเปญ
            </p>
            <div className='mt-5 text-sm font-light text-secondary text-center'>
              <p>หากคุณออกจากหน้านี้ ข้อมูลที่กรอกไว้จะหายไป</p>
              {canSaveDraft && <p>คุณต้องการที่จะบันทึกแบบร่างไว้หรือไม่</p>}
            </div>
          </div>
        ),
        onConfirm: async () => {
          await createDraft(values)
          confirmModal.hide()
          navigate(-1)
        },
        onClose: () => {
          confirmModal.hide()
          navigate(-1)
        },
      })
    }

    const componentProps: CreateCampaignPageProps = {
      campaignType,
      initialValues,
      handleSubmit,
      handleLeave,
    }

    return <Component {...componentProps} />
  }

  return WithCreateCampaignPage
}
